import {Message} from "../types/Message";
import {Company} from "../types/Company";
import {CompanyDetails} from "../types/CompanyDetails";
import {User} from "../types/User";
import { CompanyStatus } from "../types/CompanyStatuses";
import { jwtDecode } from "jwt-decode";

//"http://localhost:8080"
const API_URL = "https://login.timesapp.biz";

const getValidToken = (): string | null => {
    const token = localStorage.getItem("authToken");
    if (!token) return null;

    try {
        const decoded: { exp: number } = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);
        if (decoded.exp - 300 > now) {
            return token;
        }
    } catch (e) {
        console.warn("Invalid token:", e);
    }

    return null;
};

export const getBusinesses = async (businessTypeId: string): Promise<any[]> => {
    try {
        const token = getValidToken();

        const response = await fetch(`${API_URL}/api/business/get-all-businesses`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ business_type_id: businessTypeId })
        });
        if (!response.ok) {
            throw new Error("Failed to fetch businesses");
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        console.error("Error fetching businesses:", error);
        return [];
    }
};

export const getUserMessageTypes = async (userId: number): Promise<any[]> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/whatsapp/get-user-message-types`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ userId })
        });
        if (!response.ok) {
            throw new Error("Failed to fetch user message types");
        }
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
            // Map the server response to a more frontend-friendly format if needed
            return data.data.map((item: any) => ({
                user_id: item.user_id,
                message_type_id: item.message_type_id,
                is_active: item.is_active,
            }));
        } else {
            return [];
        }
    } catch (error) {
        console.error("Error fetching user message types:", error);
        return [];
    }
};

export const setUserMessageTypes = async (userId: number, messageTypes: any[]): Promise<any[]> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/whatsapp/set-user-message-types`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ userId, messageTypes })
        });
        if (!response.ok) {
            throw new Error("Failed to set user message types");
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        console.error("Error setting user message types:", error);
        return [];
    }
}

export const handleBudgetLogic = async (companyId: string): Promise<{ success: boolean }> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/handle-budget-logic`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ companyId })
        });

        if (!response.ok) {
            throw new Error("Failed to handle budget logic");
        }

        const data = await response.json();
        return { success: data.success };
    } catch (error) {
        console.error("❌ Error in handleBudgetLogic:", error);
        return { success: false };
    }
};

export const fetchMessagesByCompanyId = async (companyId: string): Promise<Message[]> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/openai/get-messages`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ id: companyId })
        });

        if (!response.ok) {
            throw new Error("Failed to retrieve messages");
        }

        const data = await response.json(); // Parse JSON response

        // Validate response format
        if (!data.success || !Array.isArray(data.data)) {
            throw new Error("Invalid response format");
        }

        // Transform API response to match the frontend's required format
        return data.data.map((msg: any) => ({
            role: msg.role === "user" ? "user" : "assistant",
            content: msg.content[0]?.text?.value || "", // Extracting text content safely
            id: msg.id,
            created_at: msg.created_at,
            metadata: msg.metadata ?? null,
        })).reverse();

    } catch (error) {
        console.error("Error fetching messages:", error);
        return []; // Return an empty array on failure
    }
};

export const fetchNewCompanies = async (): Promise<Company[]> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-new-companies`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
        });

        if (!response.ok) {
            throw new Error("Failed to fetch new companies");
        }

        const companiesResponse = await response.json();

        // Map the response to match the Company type
        return companiesResponse.map((company: any) => ({
            id: company.id,
            name: company.name,
            organization_number: company.organization_number,
            thread_id: company.thread_id,
            created_at: company.created_at,
            updated_at: company.updated_at,
            is_use_interpreter: company.is_use_interpreter,
            done_date: company.done_date || '', // Ensure it's an empty string if null
            billing_amount: company.billing_amount || '', // Handle optional field
            occupation: company.occupation || '', // Handle optional field
            operator_user_id: company.operator_user_id,
            company_details: company.company_details || {} as CompanyDetails, // Default to empty object if null
            status_description: company.status_description || null,
            status: company.status ? {
                status: company.status,
                status_name: null,  // Assuming "status" is both the status and its name
                color: null,  // Default color, adjust if needed
                operator_user_id: null,
            } : {} as CompanyStatus, // Default to empty if no status
        }));
    } catch (error) {
        console.error("Error fetching new companies:", error);
        return []; // Return an empty array on failure
    }
};


export const getCompanyStatuses = async (): Promise<CompanyStatus[]> => {
    const token = getValidToken();
    const response = await fetch(`${API_URL}/api/companies/get-company-statuses`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        }
    });

    const data = await response.json();
    return data.map((status: any) => ({
        status: status.status,
        status_name: status.status_name,
        color: status.color,
        operator_user_id: Number(status.operator_user_id)
    }));
}


export const addUser = async (
    userData: { firstName: string; lastName: string; phone: string; role: string; companyId: number, isMessageContact: boolean, isVoiceMessage: boolean }
): Promise<{ success: boolean; message: string }> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/users/add-user`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                first_name: userData.firstName,
                last_name: userData.lastName,
                phone_number: userData.phone,
                job_title_id: userData.role,
                company_id: userData.companyId,
                is_message_contact: userData.isMessageContact,
                is_voice_messages: userData.isVoiceMessage
            })
        });

        if (!response.ok) {
            throw new Error("Failed to add user");
        }

        const data = await response.json();

        if (data.success) {
            return { success: true, message: "User added successfully" };
        } else {
            return { success: false, message: "Failed to add user" };
        }

    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        console.error("Error adding user:", errorMessage);
        return { success: false, message: errorMessage };
    }
};

export const editUser = async (
    userData: { userId: string; firstName: string; lastName: string; phone: string; role: string; isMessageContact: boolean; isVoiceMessage: boolean }
): Promise<{ success: boolean; message: string }> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/users/edit-user`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                userId: userData.userId,
                first_name: userData.firstName,
                last_name: userData.lastName,
                phone_number: userData.phone,
                job_title_id: userData.role,
                is_message_contact: userData.isMessageContact,
                is_voice_messages: userData.isVoiceMessage
            })
        });

        if (!response.ok) {
            throw new Error("Failed to edit user");
        }

        const data = await response.json();

        if (data.success) {
            return { success: true, message: "User updated successfully" };
        } else {
            return { success: false, message: "Failed to update user" };
        }
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        console.error("Error editing user:", errorMessage);
        return { success: false, message: errorMessage };
    }
};

export const getUsersByJobTitleId = async (jobTitleId: string): Promise<User[]> => {
    if (!jobTitleId) {
        console.error("⚠ No jobTitleId provided to getUsersByJobTitleId.");
        return [];
    }

    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/users/get-users-by-job-title-id`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ job_title_id: jobTitleId }) // ✅ Send body as expected in controller
        });

        if (!response.ok) {
            throw new Error(`Failed to retrieve users by jobTitleId. Status: ${response.status}`);
        }

        const data = await response.json();
        if (!data.success || !Array.isArray(data.data)) {
            throw new Error("Invalid response format: Expected an array");
        }

        return data.data.map((user: any) => ({
            id: Number(user.id),
            email: user.email,
            userId: user.userId,
            groupId: user.groupId,
            company_id: Number(user.company_id),
            first_name: user.first_name,
            last_name: user.last_name,
            job_title_id: user.job_title_id,
            phone_number: user.phone_number,
            is_message_contact: user.is_message_contact,
            is_voice_messages: user.is_voice_messages,
        }));

    } catch (error) {
        console.error("❌ Error fetching users by jobTitleId:", error);
        return [];
    }
};


export const getUsersByCompany = async (companyId: string): Promise<User[]> => {
    if (!companyId) {
        console.error("⚠ No companyId provided to getUsersByCompany.");
        return [];
    }

    try {
        console.log(`🔹 Sending request to get users for companyId: ${companyId}`);
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/users/get-users-by-company`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ company_id: companyId }) // ✅ Ensure request body matches backend expectation
        });

        if (!response.ok) {
            throw new Error(`Failed to retrieve users: ${response.statusText}`);
        }

        const users = await response.json(); // ✅ API returns an array directly, no `data` field
        console.log(`✅ Received users:`, users);

        if (!Array.isArray(users)) {
            throw new Error("Invalid response format: Expected an array");
        }

        return users.map((user: any) => ({
            id: Number(user.id), // ✅ Convert `id` to number
            email: user.email,
            userId: user.userId,
            groupId: user.groupId,
            company_id: Number(user.company_id), // ✅ Ensure `company_id` is a number
            first_name: user.first_name,
            last_name: user.last_name,
            job_title_id: user.job_title_id,
            phone_number: user.phone_number,
            is_message_contact: user.is_message_contact,
            is_voice_messages: user.is_voice_messages,
        }));

    } catch (error) {
        console.error("❌ Error fetching users:", error);
        return [];
    }
};




export const deleteMessage = async (companyId: string, messageId: string): Promise<{ id: string; object: string; deleted: boolean }> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/openai/delete-message`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ companyId, messageId })
        });

        if (!response.ok) {
            throw new Error(`Failed to delete message. Status: ${response.status}`);
        }

        const data = await response.json(); // Parse JSON response

        // Validate response structure
        if (!data || typeof data !== "object" || !data.deleted) {
            throw new Error("Invalid response format");
        }

        // Return the expected structure
        return {
            id: data.id,
            object: data.object,
            deleted: data.deleted
        };

    } catch (error) {
        console.error("Error deleting message:", error);
        return { id: messageId, object: "thread.message.deleted", deleted: false }; // Fail gracefully
    }
};


export const sendMessageToCompany = async (companyId: string, message: string): Promise<Message | null> => {
    try {
        console.log('Sending message to thread called: ', new Date().toLocaleTimeString());
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/openai/send-message`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ companyId, message })
        });

        console.log('Sending message to thread finished: ', new Date().toLocaleTimeString());

        if (!response.ok) {
            throw new Error("Failed to retrieve message");
        }

        const data = await response.json(); // Parse JSON response
        console.log("API Response:", data); // Debugging output

        // ✅ Validate response format
        if (!data || typeof data !== "object") {
            throw new Error("Invalid response format: Expected an object");
        }

        // ✅ Return a **single** message object
        return {
            id: data.id,
            role: data.role as "user" | "assistant", // Ensure correct type
            content: data.content?.text?.value || "", // Extract text safely
            created_at: data.created_at
        };

    } catch (error) {
        console.error("Error sending message:", error);
        return null; // Return null on failure
    }
};

export const executeRun = async (
    companyId: string,
    originalPrompt: string,
    onStreamUpdate: (text: string) => void
): Promise<void> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/openai/execute-run`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ companyId, originalPrompt })
        });

        if (!response.ok) {
            throw new Error(`Request failed. Status: ${response.status}`);
        }

        const data = await response.json();
        if (data.text) {
            onStreamUpdate(data.text);
        }
    } catch (error) {
        console.error("❌ Error executing run:", error);
    }
};

export const updateCompany = async (companyData: {
    id: number;
    name?: string;
    occupation?: string;
    billing_amount?: string;
    operator_user_id?: string;
    status?: CompanyStatus;
    status_description?: string;
}): Promise<{ success: boolean; message: string; updatedCompany?: Company }> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/update-company`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
                id: companyData.id,
                name: companyData.name,
                occupation: companyData.occupation || null,
                billing_amount: companyData.billing_amount || null,
                operator_user_id: Number(companyData.operator_user_id) || null,
                status: companyData?.status || null,
                status_description: companyData.status_description || null,
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to update company. Status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
            return {
                success: true,
                message: data.message,
                updatedCompany: data.updatedCompany
            };
        } else {
            return {
                success: false,
                message: "Failed to update company"
            };
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        console.error("❌ Error updating company:", errorMessage);
        return {
            success: false,
            message: errorMessage
        };
    }
};

export const updateCashFlowDetails = async (companyId: string, isUpdateFile: boolean): Promise<CompanyDetails> => {
    try {
        console.log(`🔄 Updating Cash Flow File for companyId: ${companyId}...`);

        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies-files/update-cash-flow-details`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ companyId, isUpdateFile }) // ✅ Sending companyId as payload
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to update Cash Flow File. Status: ${response.status}. Server says: ${errorText}`);
        }

        const data: CompanyDetails = await response.json();

        console.log("✅ Cash Flow File updated successfully:", data);

        return data;

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        console.error("❌ Error updating Cash Flow File:", errorMessage);
        throw error;
    }
};

export const updateTotals = async (companyId: number, notAdjustedMinTotal: number, nigrarotMinTotal: number, nigrarotMinDay: number): Promise<CompanyDetails> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies-files/update-totals`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ companyId, notAdjustedMinTotal, nigrarotMinTotal, nigrarotMinDay })
        });
        if (!response.ok) {
            throw new Error(`Failed to update totals. Status: ${response.status}`);
        }
        const data: CompanyDetails = await response.json();
        
        return data;
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        console.error("❌ Error updating totals:", errorMessage);
        throw error;
    }
}

export const getActiveCompanies = async (): Promise<Company[]> => {
    const token = getValidToken();
    const response = await fetch(`${API_URL}/api/companies/get-active-companies`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
        }
    });

    const data = await response.json();

    // Map each item to a Company, converting string fields to numbers as needed.
    const result = data.map((company: any) => ({
        ...company,
    }));

    console.log(JSON.stringify(result, null, 2));
    return result;
}


export const getServerIp = async (): Promise<string | null> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/companies/get-server-ip`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch server IP. Status: ${response.status}`);
        }

        const data = await response.json();

        if (!data || typeof data.ip !== "string") {
            throw new Error("Invalid response format: Expected an object with an 'ip' string");
        }

        return data.ip; // ✅ Return the server IP as a string
    } catch (error) {
        console.error("❌ Error fetching server IP:", error);
        return null; // Return null on failure
    }
};

export const setEventStatus = async (eventId: number, status: string): Promise<{ success: boolean }> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/events/set-event-status`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ eventId, status })
        });

        if (!response.ok) {
            throw new Error("Failed to update event status");
        }

        const data = await response.json();
        return { success: data.success };
    } catch (error) {
        console.error("❌ Error setting event status:", error);
        return { success: false };
    }
};

export const getMessageById = async (companyId: string, messageId: string): Promise<any> => {
    try {
        const token = getValidToken();
        const response = await fetch(`${API_URL}/api/openai/get-message`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ companyId, messageId })
        });

        if (!response.ok) {
            throw new Error("Failed to retrieve message by ID");
        }

        const data = await response.json();

        if (data.success && data.data) {
            console.log("DATA: ",data.data);
            return data.data;
        }

        return null;
    } catch (error) {
        console.error("❌ Error in getMessageById API call:", error);
        return null;
    }
};

